import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, btnPrimary } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { toast } from "sonner";
import { FileUp, Trash2 } from "lucide-react";

const emptyForm = {
  customer_name: "", customer_phone: "", customer_email: "", loan_type: "",
  loan_amount: "", pan: "", aadhaar: "", partner_id: "", address: "", tehsil: "",
  city: "", pincode: "", notes: "",
};

export function LeadFormModal({ open, onClose, onCreated, isPartner = false, partnerId = "", editLead = null,
  products = [], lenders = [], partners = [], docTypes = [] }) {
  const [form, setForm] = useState(emptyForm);
  const [customFields, setCustomFields] = useState({});
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState(docTypes[0] || "PAN");
  const [saving, setSaving] = useState(false);
  const [fieldRules, setFieldRules] = useState({});
  const [docMaster, setDocMaster] = useState([]);

  const activeProducts = products.filter((p) => p.active);

  useEffect(() => { api.get("/public/field-rules").then((r) => setFieldRules(r.data.rules?.lead || {})).catch(() => {}); }, []);
  useEffect(() => { api.get("/doc-master").then((r) => setDocMaster(r.data || [])).catch(() => {}); }, []);

  const req = (key) => (fieldRules[key]?.required ? " *" : "");
  const isReq = (key) => !!fieldRules[key]?.required;

  const requiredDocs = docMaster
    .filter((d) => d.mandatory && (d.product === form.loan_type || d.product === "All"))
    .map((d) => d.name);

  useEffect(() => {
    if (open) {
      if (editLead) {
        setForm({ ...emptyForm, ...editLead });
        setCustomFields(editLead.custom_fields || {});
      } else {
        setForm({ ...emptyForm, loan_type: activeProducts[0]?.name || "", partner_id: isPartner ? partnerId : "" });
        setCustomFields({});
      }
      setDocs([]);
      setDocType(docTypes[0] || "PAN");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const activeFields = (products.find((p) => p.name === form.loan_type)?.fields) || [];

  const addDoc = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setDocs((d) => [...d, { doc_type: docType, file_name: file.name, data: reader.result }]);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const removeDoc = (i) => { if (!window.confirm("Remove this document from upload queue?")) return; setDocs((d) => d.filter((_, idx) => idx !== i)); };

  const submit = async (e) => {
    e.preventDefault();
    const VAL = {
      pan: [/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/, "PAN must be like ABCDE1234F"],
      phone: [/^\d{10}$/, "must be a 10-digit number"],
      aadhaar: [/^\d{12}$/, "must be a 12-digit number"],
      email: [/^[^@\s]+@[^@\s]+\.[^@\s]+$/, "must be a valid email"],
      pincode: [/^\d{6}$/, "must be a 6-digit pincode"],
      number: [/^\d+(\.\d+)?$/, "must be a number"],
    };
    for (const [key, rule] of Object.entries(fieldRules)) {
      const val = (form[key] ?? "").toString().trim();
      if (rule.required && !val) { toast.error(`${key.replace(/_/g, " ")} is required`); return; }
      if (val && rule.validation && VAL[rule.validation] && !VAL[rule.validation][0].test(val)) {
        toast.error(`${key.replace(/_/g, " ")} ${VAL[rule.validation][1]}`); return;
      }
    }
    const aadhaarDigits = (form.aadhaar || "").replace(/\D/g, "");
    if (aadhaarDigits.length !== 12) { toast.error("Aadhaar must be a 12-digit number"); return; }
    if (form.pan && !/^[A-Za-z]{5}[0-9]{4}[A-Za-z]$/.test(form.pan.trim())) { toast.error("PAN must be in the format ABCDE1234F"); return; }
    if (!(parseFloat(form.loan_amount) > 0)) { toast.error("Loan amount is required"); return; }
    if (!editLead) {
      const attached = docs.map((d) => d.doc_type);
      const missing = requiredDocs.filter((name) => !attached.includes(name));
      if (missing.length) { toast.error(`Please attach mandatory document(s): ${missing.join(", ")}`); return; }
    }
    setSaving(true);
    try {
      const payload = {
        ...form, loan_amount: parseFloat(form.loan_amount) || 0,
        aadhaar: aadhaarDigits, pan: (form.pan || "").trim().toUpperCase(),
        partner_id: (isPartner ? partnerId : form.partner_id) || null,
        custom_fields: customFields,
      };
      let lead;
      if (editLead) {
        ({ data: lead } = await api.put(`/leads/${editLead.id}`, payload));
      } else {
        ({ data: lead } = await api.post("/leads", payload));
      }
      for (const d of docs) {
        await api.post("/documents", { entity_type: "lead", entity_id: lead.id, doc_type: d.doc_type, file_name: d.file_name, data: d.data });
      }
      toast.success(editLead ? "Lead updated" : "Lead created");
      onClose();
      onCreated && onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={editLead ? "Edit Lead" : "New Lead"} size="lg" testId="add-lead-modal">
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Customer Name *</label>
          <input data-testid="lead-name-input" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Mobile *</label>
          <input data-testid="lead-phone-input" required value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Email{req("customer_email")}</label>
          <input data-testid="lead-email-input" required={isReq("customer_email")} value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Loan Type *</label>
          <select data-testid="loan-type-select" value={form.loan_type} onChange={(e) => { setForm({ ...form, loan_type: e.target.value }); setCustomFields({}); }} className={inputCls}>
            {activeProducts.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Loan Amount (₹) *</label>
          <input data-testid="lead-amount-input" required type="number" min="1" value={form.loan_amount} onChange={(e) => setForm({ ...form, loan_amount: e.target.value })} className={inputCls} />
        </div>
        {!isPartner && (
          <div>
            <label className={labelCls}>Assign to Partner</label>
            <select data-testid="lead-partner-select" value={form.partner_id} onChange={(e) => setForm({ ...form, partner_id: e.target.value })} className={inputCls}>
              <option value="">Unassigned</option>
              {partners.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.partner_type})</option>)}
            </select>
          </div>
        )}

        <div className="sm:col-span-2 border-t border-slate-100 pt-3 text-xs font-semibold uppercase tracking-wider text-slate-400">KYC Details</div>
        <div>
          <label className={labelCls}>Aadhaar (12 digit) *</label>
          <input data-testid="lead-aadhaar-input" required inputMode="numeric" maxLength={12} value={form.aadhaar}
            onChange={(e) => setForm({ ...form, aadhaar: e.target.value.replace(/\D/g, "").slice(0, 12) })} className={inputCls} placeholder="XXXXXXXXXXXX" />
        </div>
        <div>
          <label className={labelCls}>PAN{req("pan")}</label>
          <input data-testid="lead-pan-input" required={isReq("pan")} maxLength={10} value={form.pan}
            onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase().slice(0, 10) })} className={inputCls} placeholder="ABCDE1234F" />
        </div>

        <div className="sm:col-span-2 border-t border-slate-100 pt-3 text-xs font-semibold uppercase tracking-wider text-slate-400">Address</div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Address{req("address")}</label>
          <input data-testid="lead-address-input" required={isReq("address")} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Tehsil{req("tehsil")}</label>
          <input data-testid="lead-tehsil-input" required={isReq("tehsil")} value={form.tehsil} onChange={(e) => setForm({ ...form, tehsil: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>City{req("city")}</label>
          <input data-testid="lead-city-input" required={isReq("city")} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Pincode{req("pincode")}</label>
          <input data-testid="lead-pincode-input" required={isReq("pincode")} value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className={inputCls} />
        </div>

        <div className="sm:col-span-2">
          <label className={labelCls}>Notes{req("notes")}</label>
          <textarea data-testid="lead-notes-input" required={isReq("notes")} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} rows={2} />
        </div>

        {activeFields.length > 0 && (
          <div className="sm:col-span-2 border-t border-slate-100 pt-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">{form.loan_type} Details</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {activeFields.map((f) => (
                <div key={f.key}>
                  <label className={labelCls}>{f.label}{f.required && " *"}</label>
                  {f.type === "select" ? (
                    <select data-testid={`custom-field-${f.key}`} required={f.required} value={customFields[f.key] || ""} onChange={(e) => setCustomFields({ ...customFields, [f.key]: e.target.value })} className={inputCls}>
                      <option value="">Select…</option>
                      {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input data-testid={`custom-field-${f.key}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} required={f.required} value={customFields[f.key] || ""} onChange={(e) => setCustomFields({ ...customFields, [f.key]: e.target.value })} className={inputCls} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="sm:col-span-2 border-t border-slate-100 pt-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Documents</div>
          {requiredDocs.length > 0 && (
            <div className="mb-3 text-xs text-slate-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="required-docs-hint">
              Mandatory for {form.loan_type}:{" "}
              {requiredDocs.map((name) => {
                const done = docs.some((d) => d.doc_type === name);
                return <span key={name} className={`inline-flex items-center gap-1 mr-2 font-medium ${done ? "text-emerald-600" : "text-amber-700"}`}>{done ? "✓" : "•"} {name}</span>;
              })}
            </div>
          )}
          <div className="flex gap-3 items-end flex-wrap">
            <div className="flex-1 min-w-[160px]">
              <label className={labelCls}>Document Type</label>
              <select data-testid="lead-doc-type-select" value={docType} onChange={(e) => setDocType(e.target.value)} className={inputCls}>
                {[...new Set([...docTypes, ...requiredDocs])].map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <label className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm border border-slate-200 flex items-center gap-2 cursor-pointer" data-testid="lead-doc-upload-label">
              <FileUp size={16} /> Attach
              <input data-testid="lead-doc-upload-input" type="file" className="hidden" onChange={addDoc} />
            </label>
          </div>
          <div className="space-y-2 mt-3">
            {docs.map((d, i) => (
              <div key={i} data-testid={`lead-doc-row-${i}`} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                <div><div className="text-sm font-medium text-slate-800">{d.doc_type}</div><div className="text-xs text-slate-400">{d.file_name}</div></div>
                <button type="button" onClick={() => removeDoc(i)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
          <button data-testid="submit-lead-button" type="submit" disabled={saving} className={btnPrimary}>{saving ? "Saving…" : (editLead ? "Save Changes" : "Create Lead")}</button>
        </div>
      </form>
    </Modal>
  );
}
